package com.zq.webproject.ui.invite

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zq.webproject.model.InvitedUser
import org.jetbrains.compose.resources.Font
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song

// 统一字体（使用宋体，包含完整中文字符）
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 邀请用户列表页面
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun InvitedUsersScreen(
    onBackClick: () -> Unit
) {
    println("InvitedUsersScreen - 渲染邀请用户列表页面")

    val viewModel = remember { InvitedUsersViewModel() }

    // 页面加载时获取邀请用户列表
    LaunchedEffect(Unit) {
        println("InvitedUsersScreen - 开始加载邀请用户列表")
        viewModel.loadInvitedUsers()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("我的邀请", fontFamily = CaiFont, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = {
                        println("InvitedUsersScreen - 点击返回按钮")
                        onBackClick()
                    }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "返回")
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF5F5F5))
        ) {
            when {
                viewModel.isLoading -> {
                    // 加载中状态
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = Color(0xFF1E88E5))
                    }
                }
                viewModel.errorMessage != null -> {
                    // 错误状态
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = viewModel.errorMessage ?: "加载失败",
                                color = Color.Red,
                                fontSize = 16.sp,
                                fontFamily = CaiFont
                            )
                            Spacer(modifier = Modifier.height(16.dp))
                            Button(onClick = { viewModel.loadInvitedUsers() }) {
                                Text("重试", fontFamily = CaiFont)
                            }
                        }
                    }
                }
                viewModel.invitedUsers.isEmpty() -> {
                    // 空状态
                    EmptyInviteState()
                }
                else -> {
                    // 显示邀请用户列表
                    InvitedUsersList(users = viewModel.invitedUsers)
                }
            }
        }
    }
}

/**
 * 邀请用户列表
 */
@Composable
fun InvitedUsersList(users: List<InvitedUser>) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // 统计信息卡片
        item {
            InviteSummaryCard(totalUsers = users.size)
        }

        // 用户列表
        items(users) { user ->
            InvitedUserItem(user = user)
        }
    }
}

/**
 * 邀请统计卡片
 */
@Composable
fun InviteSummaryCard(totalUsers: Int) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color(0xFF1E88E5)
        ),
        elevation = CardDefaults.cardElevation(4.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(
                    text = "累计邀请",
                    fontSize = 14.sp,
                    color = Color.White.copy(alpha = 0.8f),
                    fontFamily = CaiFont
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "$totalUsers 人",
                    fontSize = 32.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    fontFamily = CaiFont
                )
            }
            Icon(
                imageVector = Icons.Default.Person,
                contentDescription = null,
                tint = Color.White.copy(alpha = 0.3f),
                modifier = Modifier.size(64.dp)
            )
        }
    }
}

/**
 * 邀请用户项
 */
@Composable
fun InvitedUserItem(user: InvitedUser) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(2.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // 头像
            Box(
                modifier = Modifier
                    .size(50.dp)
                    .background(Color(0xFF1E88E5), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.Person,
                    contentDescription = "用户头像",
                    tint = Color.White,
                    modifier = Modifier.size(28.dp)
                )
            }

            Spacer(modifier = Modifier.width(12.dp))

            // 用户信息
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = user.username ?: user.phone ?: "未知用户",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = CaiFont
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                   
                }
            }

        }
    }
}


/**
 * 空状态
 */
@Composable
fun EmptyInviteState() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Icon(
                imageVector = Icons.Default.Person,
                contentDescription = null,
                tint = Color.Gray.copy(alpha = 0.3f),
                modifier = Modifier.size(80.dp)
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = "暂无邀请用户",
                fontSize = 16.sp,
                color = Color.Gray,
                fontFamily = CaiFont
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "快去邀请好友吧～",
                fontSize = 14.sp,
                color = Color.Gray.copy(alpha = 0.7f),
                fontFamily = CaiFont
            )
        }
    }
}